/**
 * 登录入口记录工具
 *
 * 业务背景：
 * 本项目存在两个登录入口：
 * - `/signin`：默认登录页（进入后自动完成登录，供外部系统带参跳转）
 * - `/signin/new`：自定义企业登录页（公司 + 序列号 + 用户名密码）
 *
 * 两个入口的后续行为不同：
 * - 经 `/signin` 进入的用户不展示「退出应用」菜单，登录态失效后应回到 `/signin`
 * - 经 `/signin/new` 进入的用户保留「退出应用」，登出/登录态失效后回到 `/signin/new`
 *
 * 因此在登录成功时写入入口标记，消费方（退出菜单、401 跳转等）据此分支处理。
 */

const LOGIN_ENTRY_STORAGE_KEY = 'login_entry_path'

/** 默认登录页（自动登录入口） */
export const DEFAULT_SIGNIN_PATH = '/signin'
/** 自定义企业登录页 */
export const CUSTOM_SIGNIN_PATH = '/signin/new'

/**
 * 判断当前是否处于某个登录页
 * 【业务功能】登录页自身挂载时发起的探测请求（如 useIsLogin 的 /account/profile）
 *            在未登录时必然 401，这类 401 不应触发「跳转登录页」，
 *            否则会出现「自动登录已成功进入 /apps，探测请求的 401 稍后到达，
 *            又把用户从 /apps 弹回登录页」的抖动。
 * 【返回值】true 表示当前位于 /signin 或 /signin/new（含子路径）
 */
export const isOnLoginPage = (): boolean => {
  if (typeof window === 'undefined')
    return false
  const pathname = window.location.pathname
  return [DEFAULT_SIGNIN_PATH, CUSTOM_SIGNIN_PATH].some(
    path => pathname === path || pathname.startsWith(`${path}/`),
  )
}

/**
 * 记录登录入口
 * 【业务功能】登录成功后调用，把本次登录使用的页面路径持久化到 localStorage
 * 【参数含义】path：登录页路径，仅支持 DEFAULT_SIGNIN_PATH / CUSTOM_SIGNIN_PATH
 * 【异常情况】非浏览器环境（SSR）或写入失败时静默忽略，不影响登录主流程
 */
export const setLoginEntry = (path: string): void => {
  try {
    if (typeof window === 'undefined')
      return
    window.localStorage.setItem(LOGIN_ENTRY_STORAGE_KEY, path)
  }
  catch {
    // localStorage 可能被隐私模式禁用，记录失败不应阻断登录流程
  }
}

/**
 * 读取登录入口
 * 【业务功能】返回上次登录成功的页面路径
 * 【返回值】DEFAULT_SIGNIN_PATH / CUSTOM_SIGNIN_PATH；无记录时返回 CUSTOM_SIGNIN_PATH
 * 【异常情况】非浏览器环境或读取失败时按「无记录」处理
 * // Note: 无记录时默认按 /signin/new 处理，保持该功能上线前「始终显示退出应用、跳转 /signin/new」的既有行为
 */
export const getLoginEntry = (): string => {
  try {
    if (typeof window === 'undefined')
      return CUSTOM_SIGNIN_PATH
    const entry = window.localStorage.getItem(LOGIN_ENTRY_STORAGE_KEY)
    if (entry === DEFAULT_SIGNIN_PATH || entry === CUSTOM_SIGNIN_PATH)
      return entry
    return CUSTOM_SIGNIN_PATH
  }
  catch {
    return CUSTOM_SIGNIN_PATH
  }
}

/**
 * 判断是否经默认登录页 /signin 进入
 * 【业务功能】用于控制「退出应用」菜单显隐：经 /signin 进入时不展示该菜单
 * 【返回值】true 表示入口为 /signin，false 表示入口为 /signin/new 或无记录
 */
export const isEnteredViaDefaultSignin = (): boolean =>
  getLoginEntry() === DEFAULT_SIGNIN_PATH

/**
 * 获取登录态失效/登出后应跳转的登录页路径
 * 【业务功能】401 跳转、退出应用等场景统一从这里取登录页，保证与登录入口一致
 * 【返回值】'/signin'（经默认页登录）或 '/signin/new'（经自定义页登录/无记录）
 */
export const getLoginRedirectPath = (): string =>
  isEnteredViaDefaultSignin() ? DEFAULT_SIGNIN_PATH : CUSTOM_SIGNIN_PATH
